import { appSupportRoutes } from "./appSupportRoutes.js";
import { businessProfiles, bbbProfileURL } from "./siteContent.js";
import { newsArticles } from "./publishedArticleIndex.generated.js";
import { appPolicies } from "./appPolicyIndex.generated.js";
export const seoPages = {
  "/app-support": {
    noindex: true,
    title: "Application Support & Feedback | Karalewich",
    description: "Get help or send feedback for Karalewich applications, including StoveWatch, FitCheck and Mini Hockey Scoreboard."
  },
  "/apps": {
    title: "App Portfolio | FitCheck, StoveWatch & More | Karalewich",
    description: "Explore original screenshots and product previews from Karalewich’s app portfolio, including the TIAA consulting concept, FitCheck, StoveWatch, Mini Hockey Scoreboard and VEL."
  },
  "/news": {
    title: "News & Product Updates | Karalewich",
    description: "Read Karalewich’s published articles on websites, design and SEO, plus original product updates from our application portfolio."
  },
  "/sitemap": {
    title: "Sitemap | Explore Karalewich",
    description: "Browse Karalewich’s software, AI and automation, company information, development process, contact details, and privacy policy."
  },
  "/": {
    title: "Custom Software Development & AI Solutions | Karalewich",
    description: "Custom software development for established businesses. New Jersey-based Karalewich builds business applications, portals, and AI automation."
  },
  "/software": {
    title: "Custom Business Software, Web Apps & Portals | Karalewich",
    description: "Replace spreadsheets and PDF paperwork with custom business software. Quoting systems, digital forms, customer portals, and database-driven websites."
  },
  "/ai-automation": {
    title: "Business AI Assistants & Workflow Automation | Karalewich",
    description: "Put company knowledge to work with internal AI assistants, customer-facing assistants, and workflow automation. Built around your business processes."
  },
  "/about": {
    title: "Vasili Karalewich | Software Developer & Founder",
    description: "Meet Vasili Karalewich: building software since 2018, with an Electrical and Computer Engineering degree from Stevens. Your direct development partner."
  },
  "/how-we-work": {
    title: "An Ongoing Software Development Partnership | Karalewich",
    description: "Start with discovery, understand your processes, and build useful software together. A consistent development partnership from planning through ongoing improvements."
  },
  "/contact": {
    title: "Discuss Your Business Software Needs | Karalewich",
    description: "Talk about your business processes, systems, and opportunities to save time. Start a discovery conversation with Vasili. No software specification required."
  },
  "/privacy": {
    title: "Privacy & Analytics Choices | Karalewich",
    description: "How Karalewich handles inquiries, optional analytics, and your privacy choices."
  }
};
for (const policy of appPolicies) seoPages[`/${policy.slug}`] = {
  noindex: true,
  title: `${policy.title} | Karalewich`,
  description: policy.description
};
for (const route of appSupportRoutes) seoPages[route.path] = { title: `${route.app} Support | Karalewich`, description: `Support and feedback for ${route.app}.`, noindex: true };
for (const article of newsArticles) {
  seoPages[`/news/${article.slug}`] = {
    title: `${article.title} | Karalewich`,
    description: article.excerpt.replace(/\s*\[.*$/, "").slice(0, 165),
    type: "article",
    image: article.image,
    publishedTime: article.publishAt || `${article.date}T12:00:00Z`
  };
}
export function applyPageSEO(pathname) {
  const page = seoPages[pathname];
  const title = page?.title || (/^\/(admin|login)(\/|$)/.test(pathname) ? "Private Analytics | Karalewich" : "Page Not Found | Karalewich");
  const description = page?.description || "Karalewich builds custom business software and AI solutions.";
  const origin = (process.env.REACT_APP_SITE_URL || "https://karalewich.com").replace(/\/$/, "");
  const url = `${origin}${pathname}`;
  const meta = (attribute, name, content) => {
    let element = document.head.querySelector(`meta[${attribute}="${name}"]`);
    if (!element) {
      element = document.createElement("meta");
      element.setAttribute(attribute, name);
      document.head.appendChild(element);
    }
    element.setAttribute("content", content);
  };
  document.title = title;
  meta("name", "description", description);
  meta("name", "robots", page && !page.noindex ? "index,follow" : "noindex,follow");
  for (const prefix of ["og", "twitter"]) {
    const attribute = prefix === "og" ? "property" : "name";
    meta(attribute, `${prefix}:title`, title);
    meta(attribute, `${prefix}:description`, description);
    meta(attribute, `${prefix}:image`, `${origin}${page?.image || "/brand/social-card.png"}`);
  }
  meta("property", "og:url", url);
  meta("property", "og:type", page?.type || "website");
  document.head.querySelector('meta[property="article:published_time"]')?.remove();
  if (page?.publishedTime) meta("property", "article:published_time", page.publishedTime);
  document.querySelector('link[rel="canonical"]')?.setAttribute("href", url);
  document.getElementById("kw-business-schema")?.remove();
  if (page) {
    const schema = document.createElement("script");
    schema.id = "kw-business-schema";
    schema.type = "application/ld+json";
    schema.textContent = JSON.stringify(businessSchema(pathname, origin));
    document.head.appendChild(schema);
  }
  return title;
}
export function businessSchema(pathname, origin) {
  const {
    title,
    description
  } = seoPages[pathname];
  const url = origin + pathname;
  const article = newsArticles.find(item => pathname === `/news/${item.slug}`);
  return {
    "@context": "https://schema.org",
    "@graph": [...(article ? [{
      "@type": "BlogPosting",
      "@id": `${url}#article`,
      headline: article.title,
      description: article.excerpt,
      datePublished: article.publishAt || article.date,
      author: {
        "@id": `${origin}/#organization`
      },
      publisher: {
        "@id": `${origin}/#organization`
      },
      mainEntityOfPage: {
        "@id": `${url}#webpage`
      },
      image: `${origin}${article.image}`,
      ...(article.category ? {
        articleSection: article.category
      } : {})
    }] : []), {
      "@type": "Organization",
      "@id": `${origin}/#organization`,
      name: "Karalewich",
      sameAs: [...businessProfiles.map(([, url]) => url), bbbProfileURL],
      url: `${origin}/`,
      logo: `${origin}/brand/wordmark.svg`,
      email: "hello@karalewich.com",
      telephone: "+1-201-500-5343",
      description: seoPages["/"].description,
      founder: {
        "@id": `${origin}/about#vasili`
      },
      location: {
        "@type": "Place",
        name: "New Jersey"
      }
    }, {
      "@type": "Person",
      "@id": `${origin}/about#vasili`,
      name: "Vasili Karalewich",
      url: `${origin}/about`,
      jobTitle: "Founder and Software Developer",
      worksFor: {
        "@id": `${origin}/#organization`
      },
      alumniOf: {
        "@type": "CollegeOrUniversity",
        name: "Stevens Institute of Technology"
      }
    }, {
      "@type": "WebPage",
      "@id": `${url}#webpage`,
      url,
      name: title,
      description,
      publisher: {
        "@id": `${origin}/#organization`
      }
    }]
  };
}
